import React, { useState, memo, useEffect } from 'react'
import { Controller } from 'react-hook-form'
import clsx from 'clsx'
import {
  Box,
  Button,
  TextField,
} from '@mui/material'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import NumberFormat from 'react-number-format'
import MenuItem from '@mui/material/MenuItem'
import { FormattedMessage, useIntl } from 'react-intl'
import Text, { TEXT_TYPES } from 'components/designSystem/Text'
import {
  getTouchedStatus,
  getErrorEmptyStatus,
  formattedAddress,
  convertToSlug,
  isAstrollantas,
} from 'utils/miscellaneous'
import { useSchedule } from 'hooks/useSchedule'
import { formatDate, formatHour, sameDay } from 'utils/date'
import { useRouter } from 'next/router'
import Map from 'components/shared/Map'
import { useModal } from 'components/shared/Modal'
import usePageProps from 'hooks/usePageProps'
import useSellerInformation from './useSellerInformation'
import sx from './styles'

const nextYear = new Date().getFullYear() + 1

const EasyForm = ({ locations = [], onSubmit }) => {
  const router = useRouter()
  const { formatMessage: f } = useIntl()
  const [clickedInputs, setIsClickedInputs] = useState({})
  const [officeSelected, setOfficeSelected] = useState(null)
  const { handleOpenModal } = useModal()
  const [officeLink, setOfficeLink] = useState('')
  const { mapsLocations } = usePageProps()
  const {
    control,
    setFormValue,
    getValues,
    name,
    phone,
    lastname,
    year,
    maker,
    model,
    office,
    day,
    time,
    formState,
  } = useSellerInformation()
  const { getScheduleByBranch, getTimes } = useSchedule()
  const { data: schedules } = getScheduleByBranch(
    officeSelected?.id,
    false,
    Boolean(officeSelected),
  )

  const handleSubmit = () => {
    const data = getValues()
    const formatedData = {
      year: data.year,
      make: data.maker,
      model: data.model,
      name: `${data.name} ${data.lastname}`,
      phone: data.phone,
      scheduling_date: data.day,
      scheduling_time: data.time,
      inspection_location_id: officeSelected.branch_id,
      inspection_location: data.office,
      ...router?.query,
    }
    onSubmit(formatedData)
  }

  const onChangeOffice = (e) => {
    const { value } = e.target
    const activeLocation = locations.find((item) => item.name === value)
    setOfficeSelected(activeLocation)
    setFormValue('office', value, { shouldValidate: true })
    if (day) {
      setFormValue('day', '', { shouldValidate: true })
    }
    if (time) {
      setFormValue('time', '', { shouldValidate: true })
    }
  }

  const handleMapModal = () => {
    handleOpenModal({
      body: (
        <>
          <Box
            alignText="center"
            mt={2}
          >
            <Typography
              variant="body2"
              sx={sx.address}
            >
              {formattedAddress(officeSelected?.address)}
            </Typography>
          </Box>
          <Box mt={2}>
            <Map
              location={{
                lat: parseFloat(officeSelected?.address?.latitude),
                lng: parseFloat(officeSelected?.address?.longitude),
              }}
              zoomLevel={15}
            />
          </Box>
        </>
      ),
      configProps: {
        isFormattedTitle: true,
        showClose: true,
        scroll: 'body',
        isDividerHeader: true,
        maxWidth: 'md',
        sxProps: sx.modalContainer,
      },
    })
  }

  useEffect(() => {
    if (officeSelected?.branch_id === 7 || officeSelected?.branch_id === 10) {
      setOfficeLink(mapsLocations[0])
    } else if (officeSelected?.branch_id === 1 || officeSelected?.branch_id === 4) {
      setOfficeLink(mapsLocations[1])
    }
  }, [officeSelected])

  // const directionOffice = (value) => {
  //   const location = branches.find((item) => item.name === value)
  // }

  return (
    <Box>
      <Text
        type={TEXT_TYPES.heading.h4}
        my={2}
      >
        <FormattedMessage id="EASY_FORM_SUBTITLE" />
      </Text>

      <Box sx={sx.containerFields}>
        <Text
          type={TEXT_TYPES.heading.h3}
        >
          <FormattedMessage id="EASY_FORM_SELLER_DATA" />
        </Text>
        <Box sx={sx.contentController}>
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                sx={sx.field}
                placeholder={f({ id: 'EASY_FORM_NAME' })}
                label={`${f({ id: 'EASY_FORM_NAME' })}`}
                value={field.value}
                required
                onChange={field.onChange}
                fullWidth
                error={fieldState.invalid}
                className={clsx(
                  'Mui-style',
                  { touched: getTouchedStatus(fieldState, clickedInputs.name) },
                  { 'error-empty': getErrorEmptyStatus(fieldState, name) },
                )}
                helperText={fieldState.error && fieldState.error.message}
                onClick={() => setIsClickedInputs({ ...clickedInputs, name: true })}
              />
            )}
          />
        </Box>
        <Box sx={sx.contentController}>
          <Controller
            name="lastname"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                sx={sx.field}
                placeholder={f({ id: 'EASY_FORM_LASTNAME' })}
                label={`${f({ id: 'EASY_FORM_LASTNAME' })}`}
                value={field.value}
                required
                onChange={field.onChange}
                fullWidth
                error={fieldState.invalid}
                className={clsx(
                  'Mui-style',
                  { touched: getTouchedStatus(fieldState, clickedInputs.lastname) },
                  { 'error-empty': getErrorEmptyStatus(fieldState, lastname) },
                )}
                helperText={fieldState.error && fieldState.error.message}
                onClick={() => setIsClickedInputs({ ...clickedInputs, lastname: true })}
              />
            )}
          />
        </Box>
        <Box sx={sx.contentController}>
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <NumberFormat
                {...field}
                customInput={TextField}
                sx={sx.field}
                value={field.value}
                placeholder={f({ id: 'EASY_FORM_WHATSAPP' })}
                label={`${f({ id: 'EASY_FORM_WHATSAPP' })}`}
                required
                className={clsx(
                  'Mui-style',
                  { touched: getTouchedStatus(fieldState, clickedInputs.phone) },
                  { 'error-empty': getErrorEmptyStatus(fieldState, phone) },
                )}
                onChange={field.onChange}
                allowNegative={false}
                isNumericString
                isAllowed={({ value }) => value.length <= 10}
                fullWidth
                type="tel"
                error={fieldState.invalid}
                prefix=""
                helperText={fieldState.error && fieldState.error.message}
                onClick={() => setIsClickedInputs({ ...clickedInputs, phone: true })}
              />
            )}
          />
        </Box>
        <Divider />

        <Text
          type={TEXT_TYPES.heading.h3}
          my={2}
        >
          <FormattedMessage id="EASY_FORM_AUTO_DATA" />
        </Text>
        <Box sx={sx.contentController}>
          <Controller
            name="year"
            control={control}
            render={({ field, fieldState }) => (
              <NumberFormat
                {...field}
                customInput={TextField}
                sx={sx.field}
                value={field.value}
                placeholder={f({ id: 'EASY_FORM_YEAR' })}
                label={`${f({ id: 'EASY_FORM_YEAR' })}`}
                required
                className={clsx(
                  'Mui-style',
                  {
                    touched: getTouchedStatus(fieldState, clickedInputs.year),
                    'Mui-error': field.value && field.value.length < 4,
                  },
                )}
                onChange={field.onChange}
                allowNegative={false}
                isNumericString
                isAllowed={({ value }) => value === '' || (value?.length <= 4)}
                fullWidth
                type="phone"
                error={(field.value.length < 1 && field.value > 4) || field.value > nextYear}
                prefix=""
                helperText={fieldState.error && fieldState.error.message} // TODO
                decimalScale={0}
                onClick={() => setIsClickedInputs({ ...clickedInputs, year: true })}
              />
            )}
          />
        </Box>
        <Box sx={sx.contentController}>
          <Controller
            name="maker"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                sx={sx.field}
                placeholder={f({ id: 'EASY_FORM_MARK' })}
                label={`${f({ id: 'EASY_FORM_MARK' })}`}
                value={field.value}
                required
                onChange={field.onChange}
                fullWidth
                error={fieldState.invalid}
                className={clsx(
                  'Mui-style',
                  { touched: getTouchedStatus(fieldState, clickedInputs.maker) },
                  { 'error-empty': getErrorEmptyStatus(fieldState, maker) },
                )}
                helperText={fieldState.error && fieldState.error.message}
                onClick={() => setIsClickedInputs({ ...clickedInputs, maker: true })}
              />
            )}
          />
        </Box>
        <Box sx={sx.contentController}>
          <Controller
            name="model"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                sx={sx.field}
                placeholder={f({ id: 'EASY_FORM_MODEL' })}
                label={`${f({ id: 'EASY_FORM_MODEL' })}`}
                value={field.value}
                onChange={field.onChange}
                fullWidth
                required
                error={fieldState.invalid}
                className={clsx(
                  'Mui-style',
                  { touched: getTouchedStatus(fieldState, clickedInputs.model) },
                  { 'error-empty': getErrorEmptyStatus(fieldState, model) },
                )}
                helperText={fieldState.error && fieldState.error.message}
                onClick={() => setIsClickedInputs({ ...clickedInputs, model: true })}
              />
            )}
          />
        </Box>
        <Divider />

        <Text
          type={TEXT_TYPES.heading.h3}
          my={2}
        >
          <FormattedMessage id="EASY_FORM_SCHEDULE_YOUR_APPOINTMENT" />
        </Text>

        <Box>
          {Boolean(locations) && (
            <Controller
              name="office"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <TextField
                    {...field}
                    sx={sx.field}
                    className={clsx(
                      'Mui-style',
                      { touched: getTouchedStatus(fieldState, clickedInputs.office) },
                      { 'error-empty': getErrorEmptyStatus(fieldState, office) },
                    )}
                    select
                    value={field.value}
                    onChange={onChangeOffice}
                    label={f({
                      id: 'EASY_FORM_BRANCH_OFFICE',
                    })}
                    fullWidth
                    SelectProps={{ displayEmpty: true }}
                    InputProps={{
                      className: fieldState.isTouched ? 'touched' : '',
                    }}
                  >
                    {locations.length > 0 && locations
                      .filter(
                        (option) => !(isAstrollantas(option.name)),
                      )
                      .map((option) => (
                        <MenuItem
                          key={`location-${option.branch_id}`}
                          value={option.name}
                          id={`button_insp_${convertToSlug(
                            option?.name,
                          )}`}
                        >
                          {option.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </>
              )}
            />
          )}
        </Box>

        {officeLink
          && (
            <Box
              alignText="center"
              mb={3}
            >
              <Box sx={sx.address}>
                <Button
                  onClick={handleMapModal}
                  sx={sx.seeOnMap}
                  variant="text"
                >
                  <Typography
                    sx={sx.address}
                    variant="link-text"
                  >
                    {formattedAddress(officeSelected?.address)}
                  </Typography>
                </Button>
              </Box>
            </Box>
          )}

        <Box sx={sx.twoField}>
          <Controller
            name="day"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                sx={sx.field}
                select
                disabled={!officeSelected}
                value={field.value}
                className={clsx(
                  'Mui-style',
                  { touched: getTouchedStatus(fieldState, clickedInputs.day) },
                  { 'error-empty': getErrorEmptyStatus(fieldState, day) },
                )}
                onChange={(e) => {
                  field.onChange(e)
                  setFormValue('time', '', { shouldValidate: true })
                }}
                label={f({ id: 'EASY_FORM_DAY' })}
                fullWidth
                SelectProps={{ displayEmpty: true }}
                InputProps={{
                  className: fieldState.isTouched ? 'touched' : '',
                }}
              >
                <MenuItem
                  value=""
                  style={{ display: 'none' }}
                />
                {schedules?.regular_schedule?.length > 0
                  && schedules.regular_schedule
                    .filter((item) => item.hours.length > 0)
                    .filter((item) => !sameDay(new Date(), new Date(item.week_day)))
                    .map((option) => (
                      <MenuItem
                        key={`weekday-${option.week_day}`}
                        value={option.week_day.toString()}
                      >
                        {formatDate(option.week_day)}
                      </MenuItem>
                    ))}
              </TextField>
            )}
          />
          <Controller
            name="time"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                sx={sx.field}
                select
                disabled={!officeSelected || !day}
                value={field.value}
                className={clsx(
                  'Mui-style',
                  { touched: getTouchedStatus(fieldState, clickedInputs.time) },
                  { 'error-empty': getErrorEmptyStatus(fieldState, time) },
                )}
                onChange={field.onChange}
                label={f({ id: 'EASY_FORM_HOUR' })}
                fullWidth
                SelectProps={{ displayEmpty: true }}
                InputProps={{
                  className: fieldState.isTouched ? 'touched' : '',
                }}
              >
                <MenuItem
                  value=""
                  style={{ display: 'none' }}
                />
                {officeSelected
                  && day
                  && getTimes(schedules?.regular_schedule, day).map(
                    (option) => (
                      <MenuItem
                        key={`option-${option}`}
                        value={formatHour(option.toString())}
                      >
                        {formatHour(option)}
                      </MenuItem>
                    ),
                  )}
              </TextField>
            )}
          />
        </Box>

        <Button
          id="button-easy-lead"
          sx={sx.secondaryButtom}
          variant="easy"
          target="_blank"
          rel="noopener"
          onClick={handleSubmit}
          // eslint-disable-next-line max-len
          disabled={!(formState.isValid && year.length === 4)}
        >
          {f({ id: 'EASY_FORM_CTA_ONE' })}
        </Button>
      </Box>

    </Box>
  )
}

export default memo(EasyForm)
